import { useQuery } from '@apollo/client';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import { removeDuplicatesFromRecentlyViewedJobs } from './activity-center-rules';
import {
  GetActivityCenterTotalsDocument,
  GetActivityCenterTotalsQuery,
} from './graphql/get-activity-center-totals.gql-types';
import { usePrevious } from './utils';

const EVENTS = ['jobBookmarks', 'jobVisits'];

type UseTotalsResult = {
  total: number;
  totalByEvents: Record<string, number | null>;
  previousTotalByEvents: Record<string, number | null> | undefined;
  refetch: () => void;
  setTotalByEvents: Dispatch<SetStateAction<Record<string, number | null>>>;
};

type TotalByEvents = Record<string, number | null>;

function getTotal(totalByEvents: TotalByEvents): number {
  return EVENTS.reduce((acc, event) => {
    return acc + (totalByEvents[event] ?? 0);
  }, 0);
}

function getTotalByEvents(data: GetActivityCenterTotalsQuery | undefined): {
  jobBookmarks: number | null;
  jobVisits: number | null;
} {
  const jobBookmarksEdges = data?.viewer?.jobBookmarks?.edges ?? [];
  const jobVisitsEdges = data?.viewer?.jobVisits?.edges ?? [];

  const jobBookmarksNodes = jobBookmarksEdges.flatMap((edge) => {
    const job = edge?.node.job;
    return job?.__typename === 'VisibleJob' ? [job] : [];
  });
  const jobVisitsNodes = jobVisitsEdges.flatMap((edge) => {
    const job = edge?.node.job;
    return job?.__typename === 'VisibleJob' ? [job] : [];
  });

  const deduplicatedJobVisits = removeDuplicatesFromRecentlyViewedJobs(
    jobVisitsNodes,
    jobBookmarksNodes
  );

  const jobBookmarks = jobBookmarksNodes.length ?? null;
  const jobVisits = deduplicatedJobVisits.length ?? null;

  return { jobBookmarks, jobVisits };
}

export function useTotals(skip = false): UseTotalsResult {
  const [totalByEvents, setTotalByEvents] = useState<TotalByEvents>({});
  const previousTotalByEvents = usePrevious(totalByEvents);
  const total = getTotal(totalByEvents);

  const { data, refetch } = useQuery(GetActivityCenterTotalsDocument, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    skip,
  });

  useEffect(() => {
    if (skip) return;

    const { jobBookmarks, jobVisits } = getTotalByEvents(data);

    if (
      jobBookmarks !== totalByEvents.jobBookmarks ||
      jobVisits !== totalByEvents.jobVisits
    ) {
      setTotalByEvents({ jobBookmarks, jobVisits });
    }
  }, [skip, data, totalByEvents]);

  return {
    total,
    totalByEvents,
    previousTotalByEvents,
    refetch,
    setTotalByEvents,
  };
}
